import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { customerApi } from "@/lib/entities";
import { apiError } from "@/lib/errors";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import { ClientOrgAutocomplete } from "@/components/ClientOrgAutocomplete";

/**
 * Simplified Customer form.
 * Minimum required field: display_name.
 * Everything else lives behind a collapsible "More Details" panel.
 * Duplicate warning appears when an existing customer with the same
 * display_name (case-insensitive) is found — non-blocking.
 */
export const CustomerFormDialog = ({ open, orgs, onClose, onSaved, initial }) => {
  const isEdit = !!initial?.id;

  const [form, setForm] = useState({
    display_name: initial?.display_name || "",
    legacy_phone: initial?.legacy_phone || "",
    legacy_email: initial?.legacy_email || "",
    notes: initial?.notes || "",
    // More Details — Owner-facing plain text (Pre-W3b usability fix
    // 2026-02). Existing *_organization_id remain untouched on the
    // record for Phase A relationship continuity; we neither send nor
    // clear them here.
    legal_entity_text:
      initial?.legal_entity_text ??
      initial?.legal_entity_organization?.display_name ??
      "",
    display_org_text:
      initial?.display_org_text ??
      initial?.display_organization?.display_name ??
      "",
    billing_org_text:
      initial?.billing_org_text ??
      initial?.billing_organization?.display_name ??
      "",
    trip_code: initial?.trip_code || "",
    legacy_website: initial?.legacy_website || "",
    legacy_address: initial?.legacy_address || "",
    legacy_city: initial?.legacy_city || "",
    legacy_state: initial?.legacy_state || "",
    legacy_zip: initial?.legacy_zip || "",
    legacy_cell: initial?.legacy_cell || "",
    active: initial?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [duplicate, setDuplicate] = useState(null);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Debounced duplicate check when creating a new client
  useEffect(() => {
    if (isEdit) return;
    const q = form.display_name.trim();
    if (q.length < 2) {
      setDuplicate(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await customerApi.list({ q, page_size: 5 });
        const rows = res.results || [];
        const norm = q.toLowerCase();
        const match = rows.find(
          (r) => (r.display_name || "").toLowerCase() === norm
        );
        setDuplicate(match || null);
      } catch {
        setDuplicate(null);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form.display_name, isEdit]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.display_name.trim()) return toast.error("Client name is required");
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(
        (k) => payload[k] === "" && (payload[k] = null)
      );
      const saved = isEdit
        ? await customerApi.patch(initial.id, payload)
        : await customerApi.create(payload);
      toast.success(isEdit ? "Client updated" : "Client created");
      onSaved(saved);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-lg max-h-[90vh] p-0 gap-0 flex flex-col"
        data-testid="customer-form-dialog"
      >
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="font-display">
            {isEdit ? "Edit Client" : "New Client"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-[13px] px-6 pb-6 overflow-y-auto flex-1 min-h-0">
          <Field label="Name" required>
            <input
              data-testid="cf-display-name"
              required
              autoFocus
              value={form.display_name}
              onChange={(e) => setField("display_name", e.target.value)}
              className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
              placeholder="Artist, band, or tour company"
            />
          </Field>

          {!isEdit && duplicate && (
            <div
              data-testid="cf-duplicate-warning"
              className="border border-amber-hold bg-amber-hold/15 rounded-sm p-3 flex gap-2"
            >
              <AlertTriangle size={16} className="text-amber-hold shrink-0 mt-0.5" />
              <div className="flex-1 text-[12.5px]">
                <div className="font-medium text-carbon">
                  Possible existing record: <span className="font-mono">{duplicate.customer_number}</span> · {duplicate.display_name}
                </div>
                <div className="mt-1 flex gap-3">
                  <a
                    data-testid="cf-duplicate-open"
                    href={`/customers/${duplicate.id}`}
                    className="text-coach-blue hover:underline"
                  >
                    Open Existing
                  </a>
                  <button
                    type="button"
                    data-testid="cf-duplicate-dismiss"
                    onClick={() => setDuplicate(null)}
                    className="text-steel hover:text-carbon"
                  >
                    Create Anyway
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <Field label="Phone">
              <input
                data-testid="cf-phone"
                value={form.legacy_phone}
                onChange={(e) => setField("legacy_phone", e.target.value)}
                className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
              />
            </Field>
            <Field label="Email">
              <input
                data-testid="cf-email"
                type="email"
                value={form.legacy_email}
                onChange={(e) => setField("legacy_email", e.target.value)}
                className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
              />
            </Field>
          </div>
          <Field label="Notes">
            <textarea
              data-testid="cf-notes"
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              rows={2}
              className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
            />
          </Field>

          <button
            type="button"
            data-testid="cf-more-toggle"
            onClick={() => setMoreOpen((v) => !v)}
            className="flex items-center gap-1 text-[12px] text-coach-blue hover:underline"
          >
            {moreOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            More Details {moreOpen ? "" : "(legal, billing, address, trip code)"}
          </button>

          {moreOpen && (
            <div
              data-testid="cf-more-details"
              className="border-l-2 border-coach-silver pl-3 space-y-2"
            >
              <Field
                label="Legal Entity"
                hint="Contracting entity. Free-text with Client/Organization suggestions; leave blank to use Client Name on documents."
              >
                <ClientOrgAutocomplete
                  testid="cf-legal-entity"
                  value={form.legal_entity_text}
                  onChange={(v) => setField("legal_entity_text", v)}
                  placeholder="e.g. Acme Touring, LLC"
                />
              </Field>
              <Field label="Display Org" hint="Artist / touring entity shown on tour docs.">
                <ClientOrgAutocomplete
                  testid="cf-display-org"
                  value={form.display_org_text}
                  onChange={(v) => setField("display_org_text", v)}
                  placeholder="e.g. Acme World Tour"
                />
              </Field>
              <Field label="Billing Org" hint="Recipient of invoices.">
                <ClientOrgAutocomplete
                  testid="cf-billing-org"
                  value={form.billing_org_text}
                  onChange={(v) => setField("billing_org_text", v)}
                  placeholder="e.g. Acme Business Mgmt"
                />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Trip Code">
                  <input
                    data-testid="cf-trip-code"
                    value={form.trip_code}
                    onChange={(e) => setField("trip_code", e.target.value)}
                    className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                  />
                </Field>
                <Field label="Website">
                  <input
                    data-testid="cf-website"
                    value={form.legacy_website}
                    onChange={(e) => setField("legacy_website", e.target.value)}
                    className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                  />
                </Field>
              </div>
              <Field label="Address">
                <input
                  data-testid="cf-address"
                  value={form.legacy_address}
                  onChange={(e) => setField("legacy_address", e.target.value)}
                  className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                />
              </Field>
              <div className="grid grid-cols-3 gap-2">
                <Field label="City">
                  <input
                    data-testid="cf-city"
                    value={form.legacy_city}
                    onChange={(e) => setField("legacy_city", e.target.value)}
                    className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                  />
                </Field>
                <Field label="State">
                  <input
                    data-testid="cf-state"
                    value={form.legacy_state}
                    onChange={(e) => setField("legacy_state", e.target.value)}
                    className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                  />
                </Field>
                <Field label="ZIP">
                  <input
                    data-testid="cf-zip"
                    value={form.legacy_zip}
                    onChange={(e) => setField("legacy_zip", e.target.value)}
                    className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                  />
                </Field>
              </div>
              <Field label="Alternate Phone / Cell">
                <input
                  data-testid="cf-cell"
                  value={form.legacy_cell}
                  onChange={(e) => setField("legacy_cell", e.target.value)}
                  className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                />
              </Field>
              <label className="flex items-center gap-2 text-[12px] text-carbon">
                <input
                  type="checkbox"
                  data-testid="cf-active"
                  checked={form.active}
                  onChange={(e) => setField("active", e.target.checked)}
                />
                Active
              </label>
            </div>
          )}

          <DialogFooter className="pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-[12.5px] text-steel border border-coach-silver rounded-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="cf-submit"
              disabled={saving}
              className="px-4 py-1.5 text-[12.5px] font-medium bg-coach-blue hover:bg-deep-road-navy text-white rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : isEdit ? "Save" : "Create"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, hint, required, children }) => (
  <label className="block">
    <div className="text-[11.5px] font-medium uppercase tracking-wide text-steel mb-1">
      {label} {required && <span className="text-critical-red">*</span>}
    </div>
    {children}
    {hint && <div className="text-[11px] text-steel mt-1">{hint}</div>}
  </label>
);

const OrgSelect = ({ testid, value, onChange, orgs }) => (
  <select
    data-testid={testid}
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5 bg-white"
  >
    <option value="">— None —</option>
    {(orgs || []).map((o) => (
      <option key={o.id} value={o.id}>
        {o.display_name} ({o.org_type})
      </option>
    ))}
  </select>
);
